import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { callApi, HttpMethod } from "src/services/apiService";
import styles from "./RecordPayment.module.css";

interface InvoiceOption {
  id: string;
  number: string;
  description: string;
}

interface PaymentAccount {
  paymentAccountId: number;
  accountName: string;
  accountState: string;
}

interface AccountInfo {
  billingNum: string;
  isFlCorp: boolean;
}

/**
 * Allows for recording of a payment on a billing account number.
 * Replaces the Tcl version and adds ability to specify the "Payment Account"
 * the payment is being recorded into. Eg California Columbia Bank, Florida Truist Bank,
 * etc.
 *
 * Route: /sysadmin/payment/:accountNumId
 *
 * @component
 * @returns {React.FC} A functional component that renders the payment recording form
 */
const RecordPayment: React.FC = () => {
  const { accountNumId } = useParams<{ accountNumId: string }>();
  const navigate = useNavigate();

  // Form fields
  const [billingNum, setBillingNum] = useState("");
  const [amount, setAmount] = useState("");
  const [invoiceId, setInvoiceId] = useState("");
  const [receivedDate, setReceivedDate] = useState<Date>(new Date());
  const [paymentAccountId, setPaymentAccountId] = useState("");
  const [notes, setNotes] = useState("");

  // Data
  const [invoiceOptions, setInvoiceOptions] = useState<InvoiceOption[]>([]);
  const [paymentAccounts, setPaymentAccounts] = useState<PaymentAccount[]>([]);
  const [isFlCorp, setIsFlCorp] = useState(false);

  // UI state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  // Build date/time parts from the receivedDate state for the dropdowns
  const receivedHour12 = receivedDate.getHours() % 12 || 12;
  const receivedAmPm = receivedDate.getHours() >= 12 ? "pm" : "am";

  useEffect(() => {
    if (accountNumId) {
      fetchAccountInfo();
      fetchInvoiceOptions();
      fetchPaymentAccounts();
    }
  }, [accountNumId]);

  const fetchAccountInfo = async () => {
    try {
      const response: AccountInfo = await callApi(
        `sysadmin/adjustment/accountinfo/${accountNumId}`,
        HttpMethod.GET,
      );
      setBillingNum(response.billingNum || "");
      setIsFlCorp(response.isFlCorp || false);
    } catch (err: any) {
      console.error("Error fetching account info:", err);
      setLoadError(
        "Failed to load account info. The account may not exist or you may not have access.",
      );
    }
  };

  const fetchInvoiceOptions = async () => {
    try {
      const response = await callApi(
        `payment/sysadmin/invoices/${accountNumId}`,
        HttpMethod.GET,
      );
      setInvoiceOptions(response || []);
    } catch (err: any) {
      console.error("Error fetching invoice options:", err);
      setLoadError(
        (prev) => prev || "Failed to load invoices for this account.",
      );
    }
  };

  const fetchPaymentAccounts = async () => {
    try {
      const response = await callApi(
        "payment/sysadmin/accounts",
        HttpMethod.GET,
      );
      setPaymentAccounts(response || []);
      // Select the first account by default
      if (response?.length > 0) {
        setPaymentAccountId(response[0].paymentAccountId.toString());
      }
    } catch (err: any) {
      console.error("Error fetching payment accounts:", err);
      setLoadError(
        (prev) => prev || "Failed to load payment accounts.",
      );
    }
  };

  // Update a single date/time component while preserving the rest
  const updateReceivedDate = (
    field: "month" | "day" | "year" | "hour" | "minute" | "ampm",
    value: string,
  ) => {
    setReceivedDate((prev) => {
      const d = new Date(prev);
      switch (field) {
        case "month":
          d.setMonth(parseInt(value));
          break;
        case "day":
          d.setDate(parseInt(value));
          break;
        case "year":
          d.setFullYear(parseInt(value));
          break;
        case "hour": {
          const hour12 = parseInt(value);
          const isPm = d.getHours() >= 12;
          d.setHours(isPm ? (hour12 % 12) + 12 : hour12 % 12);
          break;
        }
        case "minute":
          d.setMinutes(parseInt(value));
          break;
        case "ampm": {
          const currentHour = d.getHours();
          if (value === "pm" && currentHour < 12) {
            d.setHours(currentHour + 12);
          } else if (value === "am" && currentHour >= 12) {
            d.setHours(currentHour - 12);
          }
          break;
        }
      }
      return d;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError("");
    setSuccessMessage("");

    try {
      const paymentData = {
        accountNumId: parseInt(accountNumId!),
        amount: parseFloat(amount),
        // Send -1 for "assign to upcoming", null for blank, or the actual ID.
        // Matches Tcl: only invoiceNum == -1 sets addToUpcoming=1.
        invoiceId: invoiceId === "-1" ? -1 : invoiceId ? parseInt(invoiceId) : null,
        receivedDate: receivedDate.toISOString(),
        paymentAccountId: parseInt(paymentAccountId),
        notes,
      };

      const result = await callApi(
        "payment/sysadmin/record",
        HttpMethod.POST,
        paymentData,
      );

      // Reset form fields (mirrors Tcl's full POST reload)
      setAmount("");
      setInvoiceId("");
      setReceivedDate(new Date());
      setNotes("");
      fetchInvoiceOptions();

      setSuccessMessage(
        `Payment #${result.paymentNum} was recorded for ${billingNum}`,
      );
    } catch (err: any) {
      let errorMessage = "Failed to record payment";
      if (err.data) {
        errorMessage = err.data.message || errorMessage;
        if (err.data.error) {
          errorMessage += `\n\nError Details: ${err.data.error}`;
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      setSubmitError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
  ];

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 10 }, (_, i) => currentYear - 5 + i);
  const days = Array.from({ length: 31 }, (_, i) => i + 1);
  const hours = Array.from({ length: 12 }, (_, i) => i + 1);
  const minutes = Array.from({ length: 60 }, (_, i) => i);

  // Build the invoice selector widget.
  // The backend returns the complete option list (invoices + "Assign to upcoming"),
  // matching the Tcl's server-side approach. The frontend just renders it.
  const renderInvoiceSelector = () => {
    if (invoiceOptions.length === 0) {
      return <span className="form-control-plaintext">No outstanding invoices</span>;
    }

    return (
      <select
        className="form-select"
        id="invoiceId"
        size={4}
        value={invoiceId}
        onChange={(e) => setInvoiceId(e.target.value)}
      >
        <option value=""></option>
        {invoiceOptions.map((invoice) => (
          <option key={invoice.id} value={invoice.id}>
            {invoice.id === "-1" ? invoice.description : `${invoice.id} - ${invoice.description}`}
          </option>
        ))}
      </select>
    );
  };

  return (
    <div className={styles.container}>
      {/* Corporation banner — matches Tcl behavior */}
      {billingNum && (
        <div
          style={{
            backgroundColor: isFlCorp ? "#4CAF50" : "#2196F3",
            color: "white",
            padding: "10px",
            marginBottom: "10px",
            fontWeight: "bold",
            fontSize: "14px",
          }}
        >
          {isFlCorp ? "FLORIDA CORPORATION" : "CALIFORNIA CORPORATION"}
        </div>
      )}

      {loadError && (
        <div className="alert alert-danger" role="alert">
          <h4 className="alert-heading">Error Loading Data</h4>
          <p className="mb-0">{loadError}</p>
        </div>
      )}

      {submitError && (
        <div className="alert alert-danger" role="alert">
          <h4 className="alert-heading">Error</h4>
          <pre
            className="mb-0"
            style={{ whiteSpace: "pre-wrap", fontSize: "0.9rem" }}
          >
            {submitError}
          </pre>
        </div>
      )}

      {successMessage && (
        <div className="alert alert-success" role="alert">
          <h4 className="alert-heading">{successMessage}</h4>
        </div>
      )}

      <div className="card">
        <div className="card-body">
          <form onSubmit={handleSubmit}>
            <div className="row mb-1">
              <label
                htmlFor="billingNum"
                className={`col-sm-2 col-form-label ${styles.formLabel}`}
              >
                Billing Num:
              </label>
              <div className="col-sm-4">
                <input
                  type="text"
                  className="form-control"
                  id="billingNum"
                  value={billingNum}
                  readOnly
                />
              </div>
            </div>

            <div className="row mb-1">
              <label
                htmlFor="amount"
                className={`col-sm-2 col-form-label ${styles.formLabel}`}
              >
                <span className="text-danger">*</span> Amount:
              </label>
              <div className="col-sm-4">
                <input
                  type="number"
                  step="0.01"
                  className="form-control"
                  id="amount"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  required
                />
              </div>
            </div>

            <div className="row mb-1">
              <label
                htmlFor="invoiceId"
                className={`col-sm-2 col-form-label ${styles.formLabel}`}
              >
                <span className="text-danger">**</span> For Invoice:
              </label>
              <div className="col-sm-6">{renderInvoiceSelector()}</div>
            </div>

            <div className="row mb-1">
              <label className={`col-sm-2 col-form-label ${styles.formLabel}`}>
                Received On:
              </label>
              <div className="col-sm-10">
                <div className="row g-2">
                  <div className="col-auto">
                    <select
                      className="form-select"
                      value={receivedDate.getMonth()}
                      onChange={(e) =>
                        updateReceivedDate("month", e.target.value)
                      }
                    >
                      {months.map((month, idx) => (
                        <option key={month} value={idx}>
                          {month}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-auto">
                    <select
                      className="form-select"
                      value={receivedDate.getDate()}
                      onChange={(e) =>
                        updateReceivedDate("day", e.target.value)
                      }
                    >
                      {days.map((day) => (
                        <option key={day} value={day}>
                          {day.toString().padStart(2, "0")}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-auto">
                    <select
                      className="form-select"
                      value={receivedDate.getFullYear()}
                      onChange={(e) =>
                        updateReceivedDate("year", e.target.value)
                      }
                    >
                      {years.map((year) => (
                        <option key={year} value={year}>
                          {year}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-auto">
                    <select
                      className="form-select"
                      value={receivedHour12}
                      onChange={(e) =>
                        updateReceivedDate("hour", e.target.value)
                      }
                    >
                      {hours.map((hour) => (
                        <option key={hour} value={hour}>
                          {hour.toString().padStart(2, "0")}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-auto d-flex align-items-center">
                    <span>:</span>
                  </div>
                  <div className="col-auto">
                    <select
                      className="form-select"
                      value={receivedDate.getMinutes()}
                      onChange={(e) =>
                        updateReceivedDate("minute", e.target.value)
                      }
                    >
                      {minutes.map((minute) => (
                        <option key={minute} value={minute}>
                          {minute.toString().padStart(2, "0")}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-auto">
                    <select
                      className="form-select"
                      value={receivedAmPm}
                      onChange={(e) =>
                        updateReceivedDate("ampm", e.target.value)
                      }
                    >
                      <option value="am">am</option>
                      <option value="pm">pm</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            <div className="row mb-1">
              <label
                htmlFor="paymentAccount"
                className={`col-sm-2 col-form-label ${styles.formLabel}`}
              >
                Payment Account:
              </label>
              <div className="col-sm-6">
                <select
                  className="form-select"
                  id="paymentAccount"
                  value={paymentAccountId}
                  onChange={(e) => setPaymentAccountId(e.target.value)}
                  required
                >
                  {paymentAccounts.map((acct) => (
                    <option
                      key={acct.paymentAccountId}
                      value={acct.paymentAccountId}
                    >
                      {acct.accountName} ({acct.accountState})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="row mb-1">
              <label
                htmlFor="notes"
                className={`col-sm-2 col-form-label ${styles.formLabel}`}
              >
                Notes:
              </label>
              <div className="col-sm-10">
                <textarea
                  className="form-control"
                  id="notes"
                  rows={5}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />
              </div>
            </div>

            <div className="row mb-1">
              <div className="col-sm-10 offset-sm-2">
                (Payment information can not be edited via this interface after
                submission)
              </div>
            </div>

            <div className="row mb-1">
              <div className="col-sm-10 offset-sm-2">
                <button
                  type="submit"
                  className="btn btn-success me-3"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      Recording Payment...
                    </>
                  ) : (
                    "Record Payment"
                  )}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => navigate(-1)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </form>

          <hr className="my-4" />

          <div className="row">
            <div className="col-12">
              <div className="">
                <p>
                  <strong>*</strong> Specify amount of payment as a positive
                  number, cents optional, no dollar sign necessary
                </p>
                <p>
                  <strong>**</strong> Only select an invoice number if{" "}
                  <strong>entire</strong> invoice is covered by this payment. If
                  multiple invoices are covered, split payment up into the
                  amounts of each invoice. If you choose "Assign to upcoming
                  invoice", the payment will be applied to the next invoice when
                  it is generated and the invoice will display as paid in full.
                </p>
                <p>
                  When you record a payment for an account, our system will
                  check to see if this account has scrub or policy send blocks
                  in place that should be removed. If so, these limits will be
                  reset to the default for this account's base plan.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RecordPayment;
